import React from 'react';
import { Typography, Box, Link } from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import { LegalPageLayout, useLegalStyles } from './LegalPageLayout';

export const CookiePolicy: React.FC = () => {
  const classes = useLegalStyles();

  return (
    <LegalPageLayout title="Cookie Policy" lastUpdate="11 dicembre 2024">
      <Box className={classes.section}>
        <Typography variant="h5" className={classes.sectionTitle}>
          1. Cosa Sono i Cookie
        </Typography>
        <Typography className={classes.paragraph}>
          I cookie sono piccoli file di testo che i siti web visitati inviano al browser dell'utente, dove vengono
          memorizzati, per poi essere ritrasmessi agli stessi siti alla visita successiva. Sono utilizzati per diverse
          finalità, come permettere una navigazione efficiente tra le pagine, ricordare le preferenze dell'utente e, in
          generale, migliorare l'esperienza di navigazione.
        </Typography>
      </Box>

      <Box className={classes.section}>
        <Typography variant="h5" className={classes.sectionTitle}>
          2. Titolare del Trattamento
        </Typography>
        <Typography className={classes.paragraph}>
          <strong>Società Agricola L'orto Dei F.lli Compagnoni S.s.</strong>
          <br />
          Via Baccanina, 2 - 41037 Mirandola (MO)
          <br />
          P.IVA: 03824940369
          <br />
          Email: negozio@lortocompagnoni.it
        </Typography>
      </Box>

      <Box className={classes.section}>
        <Typography variant="h5" className={classes.sectionTitle}>
          3. Cookie e Tecnologie Utilizzate
        </Typography>
        <Typography className={classes.paragraph}>
          Il nostro sito utilizza esclusivamente cookie tecnici necessari al funzionamento del servizio e alla sicurezza
          delle transazioni. Non utilizziamo cookie di profilazione o di marketing.
        </Typography>

        <Typography variant="h6" gutterBottom style={{ marginTop: 16 }}>
          3.1 Cookie Tecnici ed Essenziali
        </Typography>
        <Typography className={classes.paragraph}>
          Questi cookie sono necessari per il corretto funzionamento del sito e non possono essere disabilitati. Non
          richiedono il consenso dell'utente in quanto indispensabili per l'erogazione dei servizi richiesti.
        </Typography>

        <table className={classes.table}>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Finalità</th>
              <th>Durata</th>
              <th>Fornitore</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>cookieBannerDismissed</td>
              <td>Memorizza la visualizzazione del banner cookie</td>
              <td>Persistente</td>
              <td>Proprio</td>
            </tr>
            <tr>
              <td>jwt</td>
              <td>Autenticazione utente (localStorage)</td>
              <td>Sessione</td>
              <td>Proprio</td>
            </tr>
            <tr>
              <td>user</td>
              <td>Dati profilo utente (localStorage)</td>
              <td>Sessione</td>
              <td>Proprio</td>
            </tr>
            <tr>
              <td>items / directSaleItems</td>
              <td>Carrello acquisti (localStorage)</td>
              <td>Persistente</td>
              <td>Proprio</td>
            </tr>
            <tr>
              <td>shopMode</td>
              <td>Preferenza modalità acquisto (localStorage)</td>
              <td>Persistente</td>
              <td>Proprio</td>
            </tr>
          </tbody>
        </table>

        <Typography variant="h6" gutterBottom style={{ marginTop: 24 }}>
          3.2 Cookie di Sicurezza e Pagamenti
        </Typography>

        <table className={classes.table}>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Finalità</th>
              <th>Durata</th>
              <th>Fornitore</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>__stripe_mid / __stripe_sid</td>
              <td>Prevenzione frodi e sicurezza pagamenti</td>
              <td>1 anno / 30 min</td>
              <td>Stripe</td>
            </tr>
            <tr>
              <td>Sentry cookies</td>
              <td>Monitoraggio errori tecnici e sicurezza del servizio</td>
              <td>Sessione</td>
              <td>Sentry.io</td>
            </tr>
          </tbody>
        </table>

        <Typography className={classes.paragraph} style={{ marginTop: 16 }}>
          <strong>Stripe:</strong> utilizziamo Stripe per l'elaborazione sicura dei pagamenti. Stripe installa cookie
          tecnici necessari per la sicurezza delle transazioni e la prevenzione delle frodi. Per maggiori informazioni:{' '}
          <Link href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy di Stripe
          </Link>
        </Typography>

        <Typography className={classes.paragraph}>
          <strong>Sentry:</strong> utilizziamo Sentry per il monitoraggio degli errori tecnici, al fine di garantire la
          sicurezza e il corretto funzionamento del servizio. Questo trattamento si basa sul nostro legittimo interesse
          alla sicurezza informatica (Art. 6.1.f GDPR). Per maggiori informazioni:{' '}
          <Link href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer">
            Privacy Policy di Sentry
          </Link>
        </Typography>
      </Box>

      <Box className={classes.section}>
        <Typography variant="h5" className={classes.sectionTitle}>
          4. Servizi di Terze Parti
        </Typography>
        <Typography className={classes.paragraph}>
          Il nostro sito utilizza alcuni servizi esterni che potrebbero installare propri cookie:
        </Typography>

        <Typography className={classes.paragraph}>
          <strong>Google Fonts:</strong> il sito utilizza il font "Raleway" caricato dai server di Google. Google
          potrebbe raccogliere dati tecnici (indirizzo IP, tipo di browser) durante il caricamento dei font. Per
          maggiori informazioni:{' '}
          <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy di Google
          </Link>
        </Typography>

        <Typography className={classes.paragraph}>
          <strong>Google Maps:</strong> nella pagina Contatti utilizziamo una mappa interattiva di Google Maps. Questo
          servizio potrebbe installare cookie per il suo funzionamento. Per maggiori informazioni:{' '}
          <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy di Google
          </Link>
        </Typography>
      </Box>

      <Box className={classes.section}>
        <Typography variant="h5" className={classes.sectionTitle}>
          5. Come Disabilitare i Cookie dal Browser
        </Typography>
        <Typography className={classes.paragraph}>
          Puoi gestire le preferenze sui cookie attraverso le impostazioni del tuo browser. Di seguito i link alle guide
          dei principali browser:
        </Typography>
        <ul className={classes.list}>
          <li>
            <Link href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">
              Google Chrome
            </Link>
          </li>
          <li>
            <Link
              href="https://support.mozilla.org/it/kb/protezione-antitracciamento-avanzata-firefox-desktop"
              target="_blank"
              rel="noopener noreferrer"
            >
              Mozilla Firefox
            </Link>
          </li>
          <li>
            <Link
              href="https://support.apple.com/it-it/guide/safari/sfri11471/mac"
              target="_blank"
              rel="noopener noreferrer"
            >
              Safari
            </Link>
          </li>
          <li>
            <Link
              href="https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
              target="_blank"
              rel="noopener noreferrer"
            >
              Microsoft Edge
            </Link>
          </li>
        </ul>
        <Typography className={classes.paragraph}>
          <strong>Attenzione:</strong> la disabilitazione di tutti i cookie potrebbe compromettere il corretto
          funzionamento del sito e impedire l'utilizzo di alcune funzionalità, come l'autenticazione e il carrello
          acquisti.
        </Typography>
      </Box>

      <Box className={classes.section}>
        <Typography variant="h5" className={classes.sectionTitle}>
          6. Ulteriori Informazioni
        </Typography>
        <Typography className={classes.paragraph}>
          Per maggiori informazioni sul trattamento dei dati personali, consulta la nostra{' '}
          <Link component={RouterLink} to="/privacy-policy">
            Informativa sulla Privacy
          </Link>
          .
        </Typography>
        <Typography className={classes.paragraph}>
          Per qualsiasi domanda relativa ai cookie, puoi contattarci all'indirizzo email negozio@lortocompagnoni.it.
        </Typography>
      </Box>
    </LegalPageLayout>
  );
};
